/**
 * The meeting behind the page: one RealtimeKit client, the local media at the
 * door, everyone around the table, hands, the host's tools and the small
 * rooms. The page subscribes to its state and calls its moves; nothing else
 * in the app knows the SDK.
 *
 * RealtimeKit web core SDK, checked 22 September 2026:
 * https://developers.cloudflare.com/realtime/realtimekit/core/
 *   init with an auth token, join and leave
 * https://developers.cloudflare.com/realtime/realtimekit/core/media-acquisition-approaches/
 *   the door's media, taken before the client and handed over as defaults.mediaHandler
 *   Neither page says what leaving does to the tracks handed over as mediaHandler. Read in the 2.0.3 source
 *   (checked 26 October 2026): leave() runs cleanupSelf, which disables the audio, video and screen, stops the
 *   tracks and destructs the self, so the door's media is dead after leaving and must be taken again.
 * https://developers.cloudflare.com/realtime/realtimekit/core/local-participant/
 *   enableAudio, enableVideo, enableScreenShare, getVideoDevices,
 *   getCurrentDevices, setDevice and mediaPermissions (checked again
 *   22 September 2026)
 * https://developers.cloudflare.com/realtime/realtimekit/core/remote-participants/events/
 *   participants.joined and its events
 * https://developers.cloudflare.com/realtime/realtimekit/core/display-active-speakers/
 *   the activeSpeaker event and lastActiveSpeaker
 * https://developers.cloudflare.com/realtime/realtimekit/core/manage-participants-in-a-session/
 *   disableAudio, disableAllAudio, kick, pin
 * https://developers.cloudflare.com/realtime/realtimekit/core/breakout-rooms/
 *   connected meetings: createMeetings, moveParticipants, deleteMeetings, and
 *   the stateUpdate event that carries the rooms and who is in them, so nothing
 *   here reads them in a loop (checked again 22 September 2026)
 * https://developers.cloudflare.com/realtime/realtimekit/collaborative-stores/
 *   the shared store the hands are kept in
 * https://developers.cloudflare.com/realtime/realtimekit/core/video-effects/
 *   background blur, through @cloudflare/realtimekit-virtual-background
 */
import RealtimeKitClient, { type LeaveRoomState, type RTKSelfMedia, type RTKStore } from "@cloudflare/realtimekit";
import RealtimeKitVideoBackgroundTransformer from "@cloudflare/realtimekit-virtual-background";
import { HOST_PRESET } from "../../../shared/live-studio";
import type { LiveJoinResponse } from "../../../shared/types";
import { deviceName } from "./checks";
import { TileLayers } from "./layers";
import { holdListeners } from "./listenBuffer";
import { LIVE_MEDIA, liveOverrides, sendsAsPhoneNow } from "./liveMedia";
import { canClean, cleaner, keepClean, wakeCleaner, wantsClean } from "./noise";
import { type DoorReadings, NO_READINGS, Readings } from "./readings";
import { firstWord } from "./table";

/**
 * Where the hands are kept. The core SDK has no raise-hand state of its own,
 * so the meeting's own shared store carries it: one key a person, the moment
 * their hand went up. Everyone sees the same order, and somebody who arrives
 * late reads the hands already in the air.
 */
const HANDS_STORE = "flashroom-hands";
/** How much of the background is softened; the SDK's own middle setting. */
const SOFTEN_STRENGTH = 50;
/** How long a short line over the table stays before it fades. */
const NOTICE_MS = 3200;
/** Their client methods take 60 calls a minute, so a turned-down call waits this long. */
const RATE_WAIT_MS = 2000;
/** How many times a turned-down call is made again before it is given up on. */
const RATE_TRIES = 2;
/** Tries to open the meeting in all, when it answers late right after the host goes live. */
const JOIN_TRIES = 3;
/**
 * How long a dropped connection is left to come back by itself before the
 * page joins again with a fresh token. Why: the kit retries on its own, and
 * only says it gave up once every try is spent.
 */
const RECONNECT_WAIT_MS = 20_000;

/** The local camera and microphone as the SDK hands them over at the door. */
type LocalMedia = Awaited<ReturnType<typeof RealtimeKitClient.initMedia>>;

type MeetingPhase = "door" | "joining" | "in" | "left" | "failed";

/**
 * Where the door stands with the browser: still asking for the camera and the
 * microphone, holding them, or turned down. The door says which, rather than
 * spinning on a button while Chrome waits for an answer.
 */
type MediaPhase = "asking" | "ready" | "refused";

/** A small room as the SDK's events hand it over. */
interface RoomDump {
  id?: string;
  title?: string;
  participants: { id?: string }[];
}

/** One person at the table. */
/** The speakers to offer: none where they cannot be chosen, or when the list will not come. */
async function speakersOf(me: RTKSelfMedia): Promise<MediaDeviceInfo[]> {
  if (!canChooseSpeaker()) return [];
  try {
    return await me.getSpeakerDevices();
  } catch {
    // Why not throw: a speaker list that fails must not take the camera and microphone lists with it.
    return [];
  }
}

/** Can this browser send a page's sound to a speaker the person picks? (Most phones cannot.) */
export function canChooseSpeaker(): boolean {
  return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
}

/**
 * Whether someone else at the table is hosting. The kit sets isHost false on everyone it builds
 * from the room's socket, which is every remote person, so the preset they joined on says it.
 */
function isHostPeer(them: { isHost?: boolean; presetName?: string }): boolean {
  return them.isHost === true || them.presetName === HOST_PRESET;
}

export interface MeetingFace {
  /** The SDK's peer id: what every SDK call names them by. */
  id: string;
  /** Ours: the registrant id, or the host's user id. A hand is kept under this. */
  who: string;
  name: string;
  mine: boolean;
  host: boolean;
  cameraOn: boolean;
  micOn: boolean;
  sharing: boolean;
  /** When their hand went up (unix ms), or null. */
  handAt: number | null;
  /** The host called on them or put them in the spotlight, for everyone. */
  pinned: boolean;
  /** The kit sends their picture to this page: in a big room, only the few it chooses. */
  video: boolean;
}

interface SmallRoom {
  id: string;
  title: string;
  here: number;
}

export interface DeviceChoice {
  id: string;
  label: string;
}

export interface MeetingState {
  phase: MeetingPhase;
  /** The connection dropped and is coming back; the table stays as it was. */
  reconnecting: boolean;
  /** Whether the browser has given the door a camera and a microphone yet. */
  media: MediaPhase;
  /** Something the person has to be told, in plain words. */
  failure: string | null;
  /** A short line over the table: usually something the room turned down. */
  notice: string | null;
  faces: MeetingFace[];
  /** Who is lit: the last person heard speaking. */
  speakingId: string | null;
  /** Whose screen is on the big tile, while it runs. */
  sharingId: string | null;
  /** Paused leaves that stretch out of the replay. */
  recording: "on" | "paused" | "off";
  mine: {
    id: string;
    micOn: boolean;
    cameraOn: boolean;
    sharing: boolean;
    softened: boolean;
    /** False where the browser cannot soften a background. */
    canSoften: boolean;
    /** The background noise switch: steady noise taken out of your microphone. */
    cleaning: boolean;
    /** False where the browser cannot run the noise filter. */
    canClean: boolean;
  };
  /** What this person's preset lets them do. */
  tools: { mute: boolean; remove: boolean; spotlight: boolean; smallRooms: boolean; backToMain: boolean };
  rooms: SmallRoom[];
  inSmallRoom: boolean;
  cameras: DeviceChoice[];
  microphones: DeviceChoice[];
  /** Empty where the browser does not let a page choose where sound plays. */
  speakers: DeviceChoice[];
  /** The camera and the microphone the pickers are standing on. */
  cameraId: string;
  micId: string;
  /** Where the room's voices play; empty is the system's own choice. */
  speakerId: string;
  readings: DoorReadings;
}

const NOBODY: MeetingState = {
  phase: "door",
  reconnecting: false,
  media: "asking",
  failure: null,
  notice: null,
  faces: [],
  speakingId: null,
  sharingId: null,
  recording: "off",
  // Why off: nothing is on until the browser has handed the camera over, and a
  // button that says "on" over a black picture is a lie.
  mine: { id: "", micOn: false, cameraOn: false, sharing: false, softened: false, canSoften: false, cleaning: false, canClean: false },
  tools: { mute: false, remove: false, spotlight: false, smallRooms: false, backToMain: false },
  rooms: [],
  inSmallRoom: false,
  cameras: [],
  microphones: [],
  speakers: [],
  cameraId: "",
  micId: "",
  speakerId: "",
  readings: NO_READINGS,
};

/** Hands in the order they went up. */
export function handsInOrder(faces: readonly MeetingFace[]): MeetingFace[] {
  return faces.filter((f) => f.handAt !== null).sort((a, b) => (a.handAt ?? 0) - (b.handAt ?? 0));
}

/** Somebody's place in line, from 1; zero when their hand is down. */
export function placeInLine(faces: readonly MeetingFace[], who: string): number {
  return handsInOrder(faces).findIndex((f) => f.who === who) + 1;
}

/** Why one device did not start, from what the SDK says about it, or null when it did. */
function permissionProblem(state: string | undefined, what: "camera" | "microphone"): string | null {
  switch (state) {
    case "DENIED":
    case "SYSTEM_DENIED":
      return `Allow the ${what} in the address bar`;
    case "NO_DEVICES_AVAILABLE":
      return `No ${what} found`;
    case "COULD_NOT_START":
      return `Another app is using the ${what}`;
    case "CANCELED":
      return `The ${what} did not start`;
    default:
      return null;
  }
}

/**
 * Whether the SDK turned a call down because too many were made: their client
 * methods take 60 a minute and answer with `ClientError [ERR0013]` over the
 * rest. The code is on the error; the words are checked too, because a wrapped
 * error keeps only its message.
 */
export function rateLimited(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if ((err as { code?: string }).code === "0013") return true;
  return err.message.includes("ERR0013") || err.message.includes("Rate Limited");
}

function problemOf(err: unknown, what: "camera" | "microphone"): string {
  const name = err instanceof DOMException ? err.name : "";
  if (name === "NotAllowedError") return `Allow the ${what} in the address bar`;
  if (name === "NotFoundError" || name === "OverconstrainedError") return `No ${what} found`;
  if (name === "NotReadableError") return `Another app is using the ${what}`;
  return `The ${what} did not start`;
}

interface MeetingOptions {
  /** Our own door: it mints a fresh token every time, and refuses when the room is shut. */
  join: () => Promise<LiveJoinResponse>;
  /** True for the host: they come in on the host preset and get the tools. */
  host: boolean;
}

function stopTracks(media: LocalMedia): void {
  media.videoTrack?.stop();
  media.audioTrack?.stop();
}

function recordingOf(state: string | undefined): MeetingState["recording"] {
  if (state === "RECORDING") return "on";
  return state === "PAUSED" ? "paused" : "off";
}

export class Meeting {
  private state: MeetingState = NOBODY;
  private readonly listeners = new Set<() => void>();
  /** What size of each person's camera the kit sends this page, by the tiles that show them. */
  private readonly layers = new TileLayers((id) => this.client?.participants.joined.get(id));
  private readonly readings = new Readings(() => this.patch({ readings: this.readings.snapshot() }));
  private client: RealtimeKitClient | null = null;
  private media: LocalMedia | null = null;
  /** The microphone and camera as they were when the meeting was left, put back when they join again. */
  private wanted: { mic: boolean; camera: boolean } | null = null;
  /** The door's media is being taken again, so a second tap does not take it twice. */
  private retaking = false;
  private hands: RTKStore | null = null;
  /**
   * Why a phone's camera is hinted "motion": on 27 Oct 2026 a phone on a squeezed upload reached the host at
   * 720 x 1280 but only 4 fps at 251 kbps, Chrome keeping the size and dropping frames. "motion" makes it keep
   * the frame rate and lower the size, so a smaller smooth face shows instead of a slideshow.
   */
  private readonly phone = sendsAsPhoneNow();
  private softener: RealtimeKitVideoBackgroundTransformer | null = null;
  /** Whether the noise filter sits on the microphone right now, so it is never added twice. */
  private cleanerOn = false;
  private stopped = false;
  /** The main room's id, read once and then kept. */
  private parent: string | null = null;
  /** The small rooms as the SDK last said them, for the moves that name one. */
  private smallRooms: RoomDump[] = [];
  private noticeTimer: number | undefined;
  private reconnectTimer: number | undefined;
  private rejoining = false;
  /** A read is already on its way for this turn of events. */
  private reading = false;

  constructor(private readonly opts: MeetingOptions) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  snapshot = (): MeetingState => this.state;

  /** The track the door's preview shows, or null before the camera starts. */
  previewTrack(): MediaStreamTrack | null {
    return this.local()?.videoTrack ?? null;
  }

  /** One person's picture, for a tile. */
  trackOf(id: string, kind: "video" | "screen"): MediaStreamTrack | null {
    if (id === this.state.mine.id) {
      const me = this.local();
      return kind === "video" ? (me?.videoTrack ?? null) : (me?.screenShareTracks.video ?? null);
    }
    const them = this.client?.participants.joined.get(id);
    if (!them) return null;
    return kind === "video" ? them.videoTrack : them.screenShareTracks.video;
  }

  /** A tile showing this person is `width` pixels wide now, or gone when it is null. */
  tileSized(id: string, key: string, width: number | null): void {
    if (id === this.state.mine.id) return;
    this.layers.seen(id, key, width);
  }

  /**
   * What one person sounds like: their microphone while it is on, and the
   * sound of a screen they share. Never your own, which would only echo back.
   */
  soundsOf(id: string): MediaStreamTrack[] {
    if (id === this.state.mine.id) return [];
    const them = this.client?.participants.joined.get(id);
    if (!them) return [];
    const sounds: MediaStreamTrack[] = [];
    if (them.audioEnabled && them.audioTrack) sounds.push(them.audioTrack);
    if (them.screenShareEnabled && them.screenShareTracks.audio) sounds.push(them.screenShareTracks.audio);
    return sounds;
  }

  // --- the door ---------------------------------------------------------
  /** Take the camera and the microphone, before there is a meeting to join. */
  async openDoor(want?: { mic: boolean; camera: boolean }): Promise<void> {
    // Why not on a second take: the browser has answered already, so the ask card must not flash again.
    if (!want) this.patch({ media: "asking" });
    try {
      const media = await RealtimeKitClient.initMedia({ video: true, audio: true, constraints: LIVE_MEDIA });
      if (this.stopped) {
        stopTracks(media);
        return;
      }
      if (want && !want.mic) await media.disableAudio();
      if (want && !want.camera) await media.disableVideo();
      this.media = media;
      this.wanted = null;
      media.on("*", () => {
        // Media that was let go of speaks no more.
        if (this.media !== media) return;
        // Only the door waits on an answer about the camera; after that the meeting's own state rules.
        if (!this.client) this.readPermissions(media);
        this.readAll();
      });
      this.readings.start(media.videoTrack ?? null, media.audioTrack ?? null);
      const cleanable = canClean();
      this.patch({
        mine: {
          ...this.state.mine,
          canSoften: RealtimeKitVideoBackgroundTransformer.isSupported(),
          canClean: cleanable,
          cleaning: cleanable && wantsClean(),
        },
      });
      this.readPermissions(media);
      void this.listDevices();
      // A headset plugged in mid-live shows up in the pickers without a reload.
      navigator.mediaDevices?.addEventListener?.("devicechange", this.devicesChanged);
    } catch (err) {
      this.readings.setProblem("camera", problemOf(err, "camera"));
      this.readings.setProblem("mic", problemOf(err, "microphone"));
      this.patch({ media: "refused" });
    }
  }

  /**
   * What the browser has said about the camera and the microphone so far. It
   * says NOT_REQUESTED until somebody answers the prompt, which is the door's
   * waiting, and one may be refused while the other is fine.
   */
  private readPermissions(media: LocalMedia): void {
    const camera = permissionProblem(media.mediaPermissions.video, "camera");
    const mic = permissionProblem(media.mediaPermissions.audio, "microphone");
    this.readings.setProblem("camera", camera);
    this.readings.setProblem("mic", mic);
    const asking =
      media.mediaPermissions.video === "NOT_REQUESTED" && media.mediaPermissions.audio === "NOT_REQUESTED";
    if (!asking) {
      // The checks say the same thing as the two buttons over the picture.
      this.readings.setOn("camera", media.videoEnabled);
      this.readings.setOn("mic", media.audioEnabled);
    }
    this.patch({
      media: asking ? "asking" : camera && mic ? "refused" : "ready",
      mine: { ...this.state.mine, micOn: media.audioEnabled, cameraOn: media.videoEnabled },
      readings: this.readings.snapshot(),
    });
  }

  /** Go in: a fresh token from our door, then the meeting itself. */
  async enter(): Promise<void> {
    if (this.state.phase === "joining" || this.state.phase === "in") return;
    this.patch({ phase: "joining", failure: null, reconnecting: false });
    try {
      // Why: after leaving, the camera and microphone were let go; they come back as they were, with live tracks.
      if (this.wanted && !this.media) await this.openDoor(this.wanted);
      if (!(await this.connect())) return;
      this.patch({ phase: "in" });
      this.readAll();
      this.readRooms();
      void this.listDevices();
    } catch (err) {
      this.patch({
        phase: "failed",
        failure: err instanceof Error ? err.message : "The meeting did not open. Try again.",
      });
    }
  }

  /**
   * The kit's client, made with a fresh token. Why the retries: right after the host presses Go live the meeting
   * answers late, the kit gives up after 3 seconds and says [ERR0001] Failed to initialize (27 Oct 2026: two
   * failures, then it worked). A token is never reused, so each try asks our door again. Null when the page went.
   */
  private async initClient(): Promise<RealtimeKitClient | null> {
    for (let attempt = 1; ; attempt++) {
      const { authToken } = await this.opts.join();
      if (this.stopped) return null;
      try {
        return await RealtimeKitClient.init({
          authToken,
          overrides: liveOverrides(this.phone),
          defaults: {
            audio: true,
            video: true,
            mediaConfiguration: LIVE_MEDIA,
            ...(this.media ? { mediaHandler: this.media } : {}),
          },
        });
      } catch (err) {
        const cold = (err as { code?: unknown } | null)?.code === "0001";
        if (!cold || attempt >= JOIN_TRIES) throw err;
        await new Promise((done) => window.setTimeout(done, attempt * 1000));
        if (this.stopped) return null;
      }
    }
  }

  /** A fresh token from our door, then the meeting itself. False when the page went meanwhile. */
  private async connect(): Promise<boolean> {
    const client = await this.initClient();
    if (!client) return false;
    if (this.stopped) {
      await client.leave();
      return false;
    }
    this.client = client;
    this.layers.rebind();
    this.wire(client);
    await client.join();
    if (this.stopped) return false;
    // Why here and not at the door: entering is a click, and a browser starts
    // the filter's sound paused on a page nobody has clicked yet.
    void this.applyClean();
    await this.keepSoften(client);
    await this.openHands(client);
    return true;
  }

  /**
   * The one read on the way in, for the host alone: which meeting is the main
   * one, and any small rooms already open after a reload. Every change after
   * this arrives on `stateUpdate`.
   */
  private readRooms(): void {
    if (this.state.tools.smallRooms) void this.parentId();
  }

  /**
   * The room let go of this page. A dropped connection comes back by itself,
   * so the table stays and says it is reconnecting; one the kit gave up on is
   * joined again with a fresh token. Ended, removed or turned away is the end.
   * Every drop in the 7 Oct 2026 live used to land on "You have left the live".
   */
  private roomLeft(state: LeaveRoomState): void {
    if (state === "disconnected") {
      this.patch({ reconnecting: true });
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = window.setTimeout(() => void this.rejoin(), RECONNECT_WAIT_MS);
      return;
    }
    if (state === "failed") {
      void this.rejoin();
      return;
    }
    // Why: moving into or out of a small room leaves one meeting for the next, and the kit joins it by itself.
    if (state === "connected-meeting") return;
    window.clearTimeout(this.reconnectTimer);
    this.patch({ phase: "left", reconnecting: false });
  }

  private roomJoined(): void {
    window.clearTimeout(this.reconnectTimer);
    if (!this.state.reconnecting || this.rejoining) return;
    this.patch({ reconnecting: false });
    this.readAll();
  }

  /** Let the old connection go and join again with a fresh token, the table on screen meanwhile. */
  private async rejoin(): Promise<void> {
    window.clearTimeout(this.reconnectTimer);
    if (this.rejoining || this.stopped || this.state.phase !== "in") return;
    this.rejoining = true;
    this.patch({ reconnecting: true });
    const want = this.choice();
    const old = this.client;
    // From here the old connection's events are not this page's any more.
    this.client = null;
    this.hands = null;
    this.parent = null;
    this.smallRooms = [];
    try {
      await old?.leave();
    } catch {
      // it is already gone
    }
    this.releaseMedia();
    try {
      await this.openDoor(want);
      if (!(await this.connect())) return;
      this.patch({ reconnecting: false });
      this.readAll();
      this.readRooms();
    } catch {
      this.releaseMedia();
      this.wanted = want;
      this.patch({
        phase: "failed",
        reconnecting: false,
        faces: [],
        failure: "The connection to the live dropped. Join again.",
      });
    } finally {
      this.rejoining = false;
    }
  }

  /** Step out of the meeting and back to the door, where the way in still is. */
  async leave(): Promise<void> {
    const client = this.client;
    if (client) this.wanted = this.choice();
    this.client = null;
    this.hands = null;
    this.parent = null;
    this.smallRooms = [];
    try {
      await client?.leave();
    } catch {
      // the room is already gone
    }
    this.releaseMedia();
    window.clearTimeout(this.reconnectTimer);
    this.patch({
      phase: "door",
      reconnecting: false,
      faces: [],
      speakingId: null,
      sharingId: null,
      rooms: [],
      inSmallRoom: false,
    });
  }

  /** The page is going: leave, and let the camera go with it. */
  async dispose(): Promise<void> {
    this.stopped = true;
    navigator.mediaDevices?.removeEventListener?.("devicechange", this.devicesChanged);
    window.clearTimeout(this.noticeTimer);
    window.clearTimeout(this.reconnectTimer);
    this.readings.stop();
    this.layers.clear();
    this.softener?.destruct();
    this.softener = null;
    await this.leave();
    this.cleanerOn = false;
  }

  /** The page may play and hear sound only after a click in it. */
  wake(): void {
    this.readings.wake();
    wakeCleaner();
  }

  // --- my own picture and sound -----------------------------------------
  async toggleMic(): Promise<void> {
    if (await this.retake({ mic: !this.state.mine.micOn })) return;
    const me = this.local();
    if (!me) return;
    const on = !this.state.mine.micOn;
    await (on ? me.enableAudio() : me.disableAudio());
    this.readings.setOn("mic", on);
    this.readAll();
  }

  async toggleCamera(): Promise<void> {
    if (await this.retake({ camera: !this.state.mine.cameraOn })) return;
    const me = this.local();
    if (!me) return;
    const on = !this.state.mine.cameraOn;
    await (on ? me.enableVideo() : me.disableVideo());
    this.readings.setOn("camera", on);
    this.readAll();
  }

  async toggleScreen(): Promise<void> {
    const me = this.client?.self;
    if (!me) return;
    if (this.state.mine.sharing) {
      await me.disableScreenShare();
    } else {
      await me.enableScreenShare();
      // Why: "detail" tells the browser to keep the text sharp and drop frames when the load rises.
      const shown = me.screenShareTracks.video;
      if (shown) shown.contentHint = "detail";
    }
    this.readAll();
  }

  /** Reduce background noise, or stop. This browser remembers the choice. */
  async toggleClean(): Promise<void> {
    if (!this.state.mine.canClean) return;
    const on = !this.state.mine.cleaning;
    keepClean(on);
    this.patch({ mine: { ...this.state.mine, cleaning: on } });
    if (this.client) await this.applyClean();
  }

  /** Put the noise filter on the microphone, or take it off, to match the switch. */
  private async applyClean(): Promise<void> {
    const me = this.local();
    const want = this.state.mine.canClean && this.state.mine.cleaning;
    if (!me || want === this.cleanerOn) return;
    this.cleanerOn = want;
    try {
      if (want) await me.addAudioMiddleware(cleaner);
      else await me.removeAudioMiddleware(cleaner);
    } catch (err) {
      this.cleanerOn = !want;
      console.warn("[live] the noise filter did not change:", err);
    }
  }

  /** Soften what is behind you, or stop. */
  async toggleSoften(): Promise<void> {
    const client = this.client;
    if (!RealtimeKitVideoBackgroundTransformer.isSupported()) return;
    // Why no blur at the door: the kit's transformer is made from the joined meeting (RealtimeKitVideoBackgroundTransformer.init
    // takes the client, and reads self.rawVideoTrack), so there is nothing documented to blur a door preview with (checked
    // in @cloudflare/realtimekit-virtual-background 0.0.2, 27 Oct 2026). The blur is offered in the room only.
    if (!client) return;
    if (this.state.mine.softened) {
      await client.self.removeAllVideoMiddlewares();
      this.patch({ mine: { ...this.state.mine, softened: false } });
      return;
    }
    await this.addSoften(client);
    this.patch({ mine: { ...this.state.mine, softened: true } });
  }

  /** The blur is made for one client (the transformer is given the meeting), so a new connection makes its own. */
  private async addSoften(client: RealtimeKitClient): Promise<void> {
    await client.self.setVideoMiddlewareGlobalConfig({ disablePerFrameCanvasRendering: true });
    this.softener?.destruct();
    this.softener = await RealtimeKitVideoBackgroundTransformer.init({ meeting: client });
    await client.self.addVideoMiddleware(await this.softener.createBackgroundBlurVideoMiddleware(SOFTEN_STRENGTH));
  }

  /** A new connection has no blur on it: put it back if they chose it, or say it is off. */
  private async keepSoften(client: RealtimeKitClient): Promise<void> {
    if (!this.state.mine.softened) return;
    try {
      await this.addSoften(client);
    } catch (err) {
      console.warn("[live] the blur did not come back:", err);
      this.patch({ mine: { ...this.state.mine, softened: false } });
    }
  }

  async chooseCamera(id: string): Promise<void> {
    await this.chooseDevice(id, "videoinput", "camera");
  }

  async chooseMic(id: string): Promise<void> {
    await this.chooseDevice(id, "audioinput", "microphone");
  }

  /** The room's voices play here from now on (RoomSound reads it). */
  chooseSpeaker(id: string): void {
    this.patch({ speakerId: id });
  }

  // --- hands ------------------------------------------------------------
  async raiseHand(): Promise<void> {
    const me = this.me();
    if (!me) return;
    await this.hands?.set(me.who, { at: Date.now() });
    this.readAll();
  }

  async lowerHand(who = this.me()?.who): Promise<void> {
    if (!who) return;
    await this.hands?.delete(who);
    this.readAll();
  }

  /**
   * The host calls on somebody: their hand goes down and their face comes to
   * the front for everyone. It cannot also turn their microphone on: the SDK
   * lets a host mute another person but never unmute them, so the person
   * called on unmutes themselves.
   */
  async callOn(id: string): Promise<void> {
    const face = this.state.faces.find((f) => f.id === id);
    if (!face) return;
    await this.lowerHand(face.who);
    await this.spotlight(id);
  }

  /**
   * The host lowers every hand at once, when more are up than anyone could call
   * on. One call for them all, because the SDK's client methods take 60 a
   * minute and a hand at a time is turned down in a busy room. `bulkDelete` is
   * the store's own, checked in @cloudflare/realtimekit 2.0.3's Store type on
   * 16 Oct 2026 (the collaborative stores page documents only `delete`).
   */
  async lowerAllHands(): Promise<void> {
    const hands = this.hands;
    const keys = handsInOrder(this.state.faces).map((face) => ({ key: face.who }));
    if (!hands || keys.length === 0) return;
    await this.rated(() => hands.bulkDelete(keys));
    this.readAll();
  }

  // --- the host's tools -------------------------------------------------
  /**
   * Mute one person. The room turns it down when their preset will not be
   * muted, a host among them, so a refusal is said in plain words rather than
   * swallowed.
   */
  async mute(id: string): Promise<void> {
    const them = this.client?.participants.joined.get(id);
    if (!them) return;
    try {
      await them.disableAudio();
    } catch {
      this.say(`${firstWord(them.name)} was not muted. The room turned it down.`);
    }
    this.readAll();
  }

  /** Mute everyone, the host included; anybody may turn their own back on. */
  async muteEveryone(): Promise<void> {
    await this.client?.participants.disableAllAudio(true);
    this.readAll();
  }

  /** Put one face at the front for everyone. */
  async spotlight(id: string): Promise<void> {
    const them = this.client?.participants.joined.get(id);
    if (them) {
      await (them.isPinned ? them.unpin() : them.pin());
    } else if (id === this.state.mine.id) {
      const me = this.client?.self;
      await (me?.isPinned ? me.unpin() : me?.pin());
    }
    this.readAll();
  }

  /**
   * Somebody the host called on goes back to their seat: their microphone goes off, and their face leaves the
   * front where the kit lets them unpin themselves (a member's preset may not; the host can still move them back).
   */
  async backToSeat(): Promise<void> {
    if (this.state.mine.micOn) await this.toggleMic();
    const me = this.client?.self;
    if (me?.isPinned) {
      try {
        await me.unpin();
      } catch {
        // the pin stays the host's to lift
      }
    }
    this.readAll();
  }

  async remove(id: string): Promise<void> {
    await this.client?.participants.joined.get(id)?.kick();
    this.readAll();
  }

  // --- small rooms ------------------------------------------------------
  /** Open `count` small rooms, and send everyone to one if that is what was asked. */
  async openSmallRooms(count: number, spread: "auto" | "by-hand"): Promise<void> {
    const client = this.client;
    if (!client) return;
    const made = await client.connectedMeetings.createMeetings(
      Array.from({ length: count }, (_, i) => ({ title: `Room ${i + 1}` })),
    );
    // The rooms are drawn from what was made, so nothing has to be read back.
    this.showRooms(made.map((m) => ({ ...m, participants: [] })));
    if (spread === "auto") {
      const parent = await this.parentId();
      const going = this.state.faces.filter((f) => !f.host);
      for (const [i, face] of going.entries()) {
        const room = made[i % made.length];
        if (parent && room.id) await client.connectedMeetings.moveParticipants(parent, room.id, [face.id]);
      }
    }
  }

  /** Put one person in one small room. */
  async sendTo(faceId: string, roomId: string): Promise<void> {
    const client = this.client;
    const parent = await this.parentId();
    if (!client || !parent) return;
    await client.connectedMeetings.moveParticipants(parent, roomId, [faceId]);
  }

  /**
   * Bring everyone back and close the small rooms, in one call: deleting a
   * connected meeting sends whoever is in it to the main room by itself
   * (breakout rooms doc, checked 22 September 2026). The table redraws from the
   * `stateUpdate` event, and the call's own answer drops the rooms at once so
   * the panel never lingers.
   */
  async closeSmallRooms(): Promise<void> {
    const client = this.client;
    const ids = this.smallRooms.map((m) => m.id).filter((id): id is string => !!id);
    if (!client || ids.length === 0) return;
    await client.connectedMeetings.deleteMeetings(ids);
    this.showRooms([]);
  }

  /** A member walks back out of their small room. */
  async backToMainRoom(): Promise<void> {
    const client = this.client;
    const parent = await this.parentId();
    const mine = this.smallRooms.find((m) => m.participants.some((p) => p.id === this.state.mine.id));
    if (!client || !parent || !mine?.id) return;
    await client.connectedMeetings.moveParticipants(mine.id, parent, [this.state.mine.id]);
  }

  // --- the wiring -------------------------------------------------------
  private me(): MeetingFace | undefined {
    return this.state.faces.find((f) => f.mine);
  }

  /** What is on right now, as the page shows it. */
  private choice(): { mic: boolean; camera: boolean } {
    return { mic: this.state.mine.micOn, camera: this.state.mine.cameraOn };
  }

  /** Let the camera and microphone go, and say so: nothing on the page may claim a camera that has stopped. */
  private releaseMedia(): void {
    const media = this.media;
    this.media = null;
    if (media) stopTracks(media);
    this.cleanerOn = false;
    this.readings.start(null, null);
    this.readings.setOn("camera", false);
    this.readings.setOn("mic", false);
    this.patch({ mine: { ...this.state.mine, micOn: false, cameraOn: false }, readings: this.readings.snapshot() });
  }

  /**
   * A tap at the door after leaving, when there is no camera or microphone held: take them again with that tap
   * applied to what they had. False when the tap is an ordinary toggle.
   */
  private async retake(change: Partial<{ mic: boolean; camera: boolean }>): Promise<boolean> {
    if (!this.wanted || this.media || this.client) return false;
    if (this.retaking) return true;
    this.retaking = true;
    try {
      await this.openDoor({ ...this.wanted, ...change });
    } finally {
      this.retaking = false;
    }
    return true;
  }

  /** The local media: the meeting's own once there is one, the door's before that. */
  private local(): RTKSelfMedia | null {
    return this.client?.self ?? this.media;
  }

  private devicesChanged = (): void => {
    void this.listDevices();
  };

  private wire(client: RealtimeKitClient): void {
    // A connection this page has let go of still talks while it closes; only the current one is heard.
    const mine = () => client === this.client;
    // Why once a turn: a big room joins with a thousand people in one go, and
    // reading everyone again for each of them froze the page.
    const changed = () => {
      if (!mine() || this.reading) return;
      this.reading = true;
      queueMicrotask(() => {
        this.reading = false;
        if (mine()) this.readAll();
      });
    };
    client.self.on("*", changed);
    client.participants.joined.on("*", changed);
    client.participants.on("activeSpeaker", ({ peerId }) => {
      if (mine()) this.patch({ speakingId: peerId });
    });
    client.recording.on("*", changed);
    // Why the event and not a read: their client methods take 60 calls a minute,
    // and a read on every change ran into that (ERR0013) until the rooms would
    // not close. `stateUpdate` carries the rooms and who is in them already.
    client.connectedMeetings.on("stateUpdate", ({ meetings }) => {
      if (mine()) this.showRooms(meetings);
    });
    client.self.on("roomLeft", ({ state }) => {
      if (mine()) this.roomLeft(state);
    });
    client.self.on("roomJoined", () => {
      if (mine()) this.roomJoined();
    });
  }

  private async makeHands(client: RealtimeKitClient): Promise<RTKStore> {
    const store = await client.stores.create(HANDS_STORE);
    // A store made a moment ago may still be catching up, so it is refreshed
    // before it is read; this is how the SDK itself reads its own stores.
    await client.stores.refresh(HANDS_STORE);
    return store;
  }

  private async openHands(client: RealtimeKitClient): Promise<void> {
    // Why twice: creating the store waits only 3 seconds for a meeting that has just started, as joining does.
    // The person is in by now, so a store that will not open is let go: hands are missing, the join stands.
    let store: RTKStore;
    try {
      store = await this.makeHands(client);
    } catch {
      try {
        store = await this.makeHands(client);
      } catch (err) {
        console.warn("The hands store did not open; the room goes on without raised hands.", err);
        return;
      }
    }
    store.subscribe("*", () => {
      if (client === this.client) this.readAll();
    });
    this.hands = store;
  }

  /**
   * The main room's id. It is read at most once a meeting, and every change
   * after that arrives on `stateUpdate`.
   */
  private async parentId(): Promise<string | null> {
    const client = this.client;
    if (this.parent || !client) return this.parent;
    const got = await this.rated(() => client.connectedMeetings.getConnectedMeetings());
    if (!got) return null;
    this.parent = got.parentMeeting.id ?? null;
    this.showRooms(got.meetings);
    return this.parent;
  }

  /**
   * One call to the connected meetings, waiting once when their rate limit
   * turns it down. Null when it still does, so a caller draws nothing rather
   * than hammering.
   */
  private async rated<T>(call: () => Promise<T>): Promise<T | null> {
    for (let tries = 0; tries < RATE_TRIES; tries++) {
      try {
        return await call();
      } catch (err) {
        if (!rateLimited(err)) throw err;
        await new Promise((done) => window.setTimeout(done, RATE_WAIT_MS * (tries + 1)));
        if (this.stopped || !this.client) return null;
      }
    }
    return null;
  }

  /** Draw the small rooms from what the SDK last said about them. */
  private showRooms(meetings: readonly RoomDump[]): void {
    this.smallRooms = [...meetings];
    const rooms = meetings.map((m, i) => ({
      id: m.id ?? String(i),
      title: m.title ?? `Room ${i + 1}`,
      here: m.participants.length,
    }));
    const inSmallRoom = meetings.some((m) => m.participants.some((p) => p.id === this.state.mine.id));
    this.patch({ rooms, inSmallRoom });
  }

  /** A short line over the table, gone again in a moment. */
  private say(words: string): void {
    this.patch({ notice: words });
    window.clearTimeout(this.noticeTimer);
    this.noticeTimer = window.setTimeout(() => this.patch({ notice: null }), NOTICE_MS);
  }

  private async listDevices(): Promise<void> {
    const me = this.local();
    if (!me) return;
    const named = (devices: MediaDeviceInfo[], word: string): DeviceChoice[] =>
      devices
        .filter((d) => d.deviceId)
        .map((d, i) => ({ id: d.deviceId, label: deviceName(d.label) || `${word} ${i + 1}` }));
    const [cameras, microphones, speakers] = await Promise.all([
      me.getVideoDevices(),
      me.getAudioDevices(),
      speakersOf(me),
    ]);
    if (this.stopped) return;
    const now = this.standingOn();
    const speakerChoices = named(speakers, "Speaker");
    this.patch({
      cameras: named(cameras, "Camera"),
      microphones: named(microphones, "Microphone"),
      speakers: speakerChoices,
      cameraId: now.camera || this.state.cameraId,
      micId: now.mic || this.state.micId,
      // A speaker unplugged hands its sound back to the system's own choice.
      speakerId: speakerChoices.some((s) => s.id === this.state.speakerId) ? this.state.speakerId : "",
    });
  }

  /** The camera and the microphone the SDK is on right now. */
  private standingOn(): { camera: string; mic: string } {
    try {
      const now = this.local()?.getCurrentDevices();
      return { camera: now?.video?.deviceId ?? "", mic: now?.audio?.deviceId ?? "" };
    } catch {
      // an older browser, or a device that went while it was being read
      return { camera: "", mic: "" };
    }
  }

  private async chooseDevice(id: string, kind: MediaDeviceKind, what: "camera" | "microphone"): Promise<void> {
    const me = this.local();
    if (!me) return;
    try {
      const devices = kind === "videoinput" ? await me.getVideoDevices() : await me.getAudioDevices();
      const device = devices.find((d) => d.deviceId === id);
      if (!device) return;
      await me.setDevice(device);
      this.readings.setProblem(what === "camera" ? "camera" : "mic", null);
      // The checks read the tracks they were given, so they are pointed at the new ones.
      this.readings.start(me.videoTrack ?? null, me.audioTrack ?? null);
      this.patch(kind === "videoinput" ? { cameraId: id } : { micId: id });
      this.readAll();
    } catch (err) {
      this.readings.setProblem(what === "camera" ? "camera" : "mic", problemOf(err, what));
    }
  }

  /** Everything the page draws, read back off the SDK in one go. */
  private readAll(): void {
    const client = this.client;
    const me = this.local();
    // Why here: every camera switch, retake at the door and rejoin ends in a read, and a new track starts with no hint.
    if (this.phone && me?.videoTrack && me.videoTrack.contentHint !== "motion") me.videoTrack.contentHint = "motion";
    const hands = (this.hands?.getAll() ?? {}) as Record<string, { at?: number } | undefined>;
    const handAt = (who: string): number | null => hands[who]?.at ?? null;
    const faces: MeetingFace[] = [];
    const hosts: MeetingFace[] = [];
    if (client) {
      const self = client.self;
      const myWho = self.customParticipantId || self.id;
      (this.opts.host ? hosts : faces).push({
        id: self.id,
        who: myWho,
        name: self.name,
        mine: true,
        host: this.opts.host,
        cameraOn: self.videoEnabled,
        micOn: self.audioEnabled,
        sharing: self.screenShareEnabled,
        handAt: handAt(myWho),
        pinned: !!self.isPinned,
        video: !!self.videoTrack,
      });
      for (const them of client.participants.joined.toArray()) {
        const who = them.customParticipantId || them.id;
        const face: MeetingFace = {
          id: them.id,
          who,
          name: them.name,
          // Why by `who`: a second tab of the same person is still that person,
          // and nobody may mute or remove themselves.
          mine: who === myWho,
          host: isHostPeer(them),
          cameraOn: them.videoEnabled,
          micOn: them.audioEnabled,
          sharing: them.screenShareEnabled,
          handAt: handAt(who),
          pinned: !!them.isPinned,
          video: !!them.videoTrack,
        };
        // The host sits at the head, then everyone else in the order they arrived.
        if (face.host) hosts.push(face);
        else faces.push(face);
      }
    }
    if (hosts.length > 0) faces.unshift(...hosts);
    this.layers.refresh();
    holdListeners(client);
    const sharer = faces.find((f) => f.sharing) ?? null;
    const permissions = client?.self.permissions;
    this.patch({
      faces,
      sharingId: sharer?.id ?? null,
      recording: recordingOf(client?.recording.recordingState),
      mine: {
        ...this.state.mine,
        id: client?.self.id ?? "",
        micOn: me?.audioEnabled ?? this.state.mine.micOn,
        cameraOn: me?.videoEnabled ?? this.state.mine.cameraOn,
        sharing: client?.self.screenShareEnabled ?? false,
        canSoften: RealtimeKitVideoBackgroundTransformer.isSupported(),
      },
      tools: {
        mute: permissions?.canDisableParticipantAudio === true,
        remove: permissions?.kickParticipant === true,
        spotlight: permissions?.pinParticipant === true,
        smallRooms: permissions?.connectedMeetings.canAlterConnectedMeetings === true,
        backToMain: permissions?.connectedMeetings.canSwitchToParentMeeting === true,
      },
    });
  }

  private patch(change: Partial<MeetingState>): void {
    this.state = { ...this.state, ...change };
    for (const listener of this.listeners) listener();
  }
}
